/* =========================================================================
   RETAILER × StockX — Add-to-Cart picker quick-add (collection/grid cards)
   Card colour swatch -> opens picker on that colour.
   Picker: pick a colour tile (swaps the preview image + filters sizes) -> pick a
   size -> the variant is added and, for NON-members, the membership variant +
   selling_plan are enrolled too, the member discount code applied, and the cart
   drawer opens — so the shopper keeps building a cart without leaving the page.

   Mirrors the PDP membership-add flow (PROTECTION A: don't double-add membership;
   PROTECTION B: skip for existing members), reading window.TRC_MEMBERSHIP. Adds go
   through window.fetch so the global membership-cap + discount monkeypatches recap.

   2026-10-18 (Owen) SHEET V2 (panels with data-sx-qa-v2, snippets/sx-quick-add): a size tap SELECTS the size;
   the sticky footer button adds it (mode "plus" = item + Retailer+ line for non-members, as before; "one" =
   item only, no membership, no code, Keevado's one-time button like its PDP; "member" = signed-in member).
   Loading -> added state on the button, the card button reads Added for a moment, the sheet slides away.
   Panels without data-sx-qa-v2 (sections/laa-drop builds its own) keep tap-to-add, unchanged.
   ========================================================================= */
(function () {
  'use strict';

  function cfg() { return window.TRC_MEMBERSHIP || {}; }

  // ---- Size ordering --------------------------------------------------------
  // Variants are rendered in Shopify's stored order, which prints sizes as plain
  // strings ("1","10","11",…,"2") and apparel out of order. Sort the size chips
  // smallest -> largest, size-aware: clothing letters by their real order, shoe /
  // numeric sizes numerically (incl. half sizes + "US 10"/"EU 43" forms). Done in
  // the DOM so colour filtering (data-sx-qa-color) still works on the moved nodes.
  var CLOTHING = {
    'xxxs': 0, '3xs': 0,
    'xxs': 1, '2xs': 1,
    'xs': 2,
    's': 3, 'sm': 3, 'small': 3,
    'm': 4, 'med': 4, 'medium': 4,
    'l': 5, 'lg': 5, 'large': 5,
    'xl': 6, '1xl': 6, 'xlarge': 6,
    'xxl': 7, '2xl': 7,
    'xxxl': 8, '3xl': 8,
    'xxxxl': 9, '4xl': 9,
    'xxxxxl': 10, '5xl': 10,
    '6xl': 11, '7xl': 12
  };
  // Returns [group, numericValue, rawLower] — lower group sorts first:
  //   0 = clothing letter, 1 = numeric (shoe/number), 2 = anything else (e.g. "One Size").
  function sizeKey(raw) {
    var s = (raw || '').trim().toLowerCase().replace(/\s+/g, '');
    if (CLOTHING[s] !== undefined) return [0, CLOTHING[s], s];
    var m = s.match(/-?\d+(\.\d+)?/);
    if (m) return [1, parseFloat(m[0]), s];
    return [2, 0, s];
  }
  function sizeCmp(a, b) {
    var x = sizeKey(a), y = sizeKey(b);
    if (x[0] !== y[0]) return x[0] - y[0];
    if (x[0] === 2) return x[2].localeCompare(y[2], undefined, { numeric: true });
    return (x[1] - y[1]) || x[2].localeCompare(y[2], undefined, { numeric: true });
  }
  function sortSizes(qa) {
    if (!qa) return;
    var grid = qa.querySelector('[data-sx-qa-sizes]');
    if (!grid || grid.hasAttribute('data-sx-qa-sorted')) return;
    var btns = Array.prototype.slice.call(grid.querySelectorAll('.sx-qa__size'));
    if (btns.length > 1) {
      btns.sort(function (p, q) { return sizeCmp(p.getAttribute('data-sx-qa-size') || p.textContent, q.getAttribute('data-sx-qa-size') || q.textContent); });
      btns.forEach(function (b) { grid.appendChild(b); });
    }
    grid.setAttribute('data-sx-qa-sorted', '');
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) { window.location.href = (window.routes && window.routes.cart_url) || '/cart'; return; }
    /* market ROOT, not /cart — a cart-drawer section rendered from /cart drops the
       express wallets (page_type guard in cart-drawer.liquid). 2026-08-24 */
    var base = ((window.routes && window.routes.cart_url) || '/cart').replace(/cart\/?$/, '');
    Promise.all([
      fetch(base + '?section_id=cart-drawer').then(function (r) { return r.text(); }),
      fetch(base + '?section_id=cart-icon-bubble').then(function (r) { return r.text(); })
    ]).then(function (res) {
      var d = new DOMParser().parseFromString(res[0], 'text/html');
      var b = new DOMParser().parseFromString(res[1], 'text/html');
      var nd = d.querySelector('#CartDrawer');
      var cd = document.getElementById('CartDrawer');
      if (nd && cd) cd.innerHTML = nd.innerHTML;
      var nb = b.querySelector('.shopify-section');
      var cb = document.getElementById('cart-icon-bubble');
      if (nb && cb) cb.innerHTML = nb.innerHTML;
      drawer.classList.remove('is-empty');
      if (typeof drawer.open === 'function') drawer.open();
    }).catch(function () {
      if (typeof drawer.open === 'function') drawer.open();
    });
  }

  // ---- Sheet v2 helpers -------------------------------------------------------
  function panelOf(qa) { return qa ? qa.querySelector('[data-sx-qa-panel]') : null; }
  function isV2(qa) { return !!(qa && qa.querySelector('[data-sx-qa-v2]')); }
  function selectedSize(qa) { return qa ? qa.querySelector('.sx-qa__size.is-selected:not(.sx-qa__size--off)') : null; }
  function setErr(qa, msg) {
    var el = qa && qa.querySelector('[data-sx-qa-err]');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  }
  // Header price, button prices and the "Only N left" line follow the picked size (else the card's lowest price).
  function paintPrice(qa) {
    var p = panelOf(qa);
    if (!p || !isV2(qa)) return;
    var mEl = p.querySelector('[data-sx-qa-mprice]'), fEl = p.querySelector('[data-sx-qa-fprice]');
    if (!p.__sxBase) {
      var fb = p.querySelector('[data-sx-qa-p="f"]');
      p.__sxBase = { m: mEl ? mEl.textContent : '', f: fEl ? fEl.textContent : (fb ? fb.textContent : '') };
    }
    var s = selectedSize(qa);
    // no size picked yet: the price of the colour on show (its first buyable size, else its first size)
    var ref = s || p.querySelector('.sx-qa__size:not(.sx-qa__size--off):not([disabled])') || p.querySelector('.sx-qa__size:not(.sx-qa__size--off)');
    var m = ref ? ref.getAttribute('data-m') : p.__sxBase.m;
    var f = ref ? ref.getAttribute('data-f') : p.__sxBase.f;
    if (mEl && m) mEl.textContent = m;
    if (fEl) { if (f && f !== m) { fEl.textContent = f; fEl.hidden = false; } else { fEl.hidden = true; } }
    p.querySelectorAll('[data-sx-qa-p]').forEach(function (el) {
      var v = el.getAttribute('data-sx-qa-p') === 'f' ? f : m;
      if (v) el.textContent = v;
    });
    var left = p.querySelector('[data-sx-qa-left]');
    if (left) {
      var q = s && s.getAttribute('data-q');
      if (q) {
        left.textContent = String(p.getAttribute('data-t-left') || '').replace('__N__', q).replace('__S__', s.getAttribute('data-sx-qa-size') || '');
        left.hidden = false;
      } else { left.hidden = true; left.textContent = ''; }
    }
    p.classList.toggle('is-ready', !!s);
    // 2026-10-18 (Owen): nothing buyable on show (e.g. a sold-out colour) -> the add button is disabled and reads Sold out.
    var buyable = !!p.querySelector('.sx-qa__size:not(.sx-qa__size--off):not([disabled])');
    p.classList.toggle('is-soldout', !buyable);
    var so = p.getAttribute('data-t-soldout') || 'Sold out';
    p.querySelectorAll('[data-sx-qa-add]').forEach(function (b) {
      if (b.classList.contains('is-loading') || b.classList.contains('is-added')) return;
      b.setAttribute('data-so', so);
      if (buyable) { b.disabled = false; b.removeAttribute('aria-disabled'); }
      else { b.disabled = true; b.setAttribute('aria-disabled', 'true'); }
    });
  }
  function selectSize(qa, btn) {
    if (!qa) return;
    qa.querySelectorAll('.sx-qa__size.is-selected').forEach(function (b) {
      b.classList.remove('is-selected'); b.setAttribute('aria-checked', 'false');
    });
    if (btn) { btn.classList.add('is-selected'); btn.setAttribute('aria-checked', 'true'); }
    setErr(qa, '');
    paintPrice(qa);
  }
  // After a colour / sleeve change: drop a selection that is no longer on show, and pick the size outright
  // when exactly one is buyable (a one-size line should not need a tap before Add).
  function settleSelection(qa) {
    if (!isV2(qa)) return;
    var cur = qa.querySelector('.sx-qa__size.is-selected');
    if (cur && cur.classList.contains('sx-qa__size--off')) cur = null;
    if (!cur) {
      var live = Array.prototype.filter.call(qa.querySelectorAll('.sx-qa__size'), function (b) {
        return !b.classList.contains('sx-qa__size--off') && !b.hasAttribute('disabled');
      });
      cur = live.length === 1 ? live[0] : null;
    }
    selectSize(qa, cur);
  }
  function nudge(qa) {
    var head = qa.querySelector('[data-sx-qa-sizehead]'), grid = qa.querySelector('[data-sx-qa-sizes]');
    [head, grid].forEach(function (el) {
      if (!el) return;
      el.classList.remove('is-nudge'); void el.offsetWidth; el.classList.add('is-nudge');
      setTimeout(function () { el.classList.remove('is-nudge'); }, 700);
    });
    if (grid && grid.scrollIntoView) { try { grid.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {} }
  }
  function showAdded(qa, btn) {
    var p = panelOf(qa);
    var txt = (p && p.getAttribute('data-t-added')) || 'Added';
    if (btn) {
      var ok = btn.querySelector('.sx-qa__add-ok');
      if (ok) ok.textContent = txt;
      btn.classList.add('is-added');
    }
    // The card's own button says Added for a moment too, so the state is still there once the drawer closes.
    var t = qa.querySelector('[data-sx-qa-open]');
    if (t && !t.__sxLabel) {
      t.__sxLabel = t.textContent;
      t.classList.add('is-added');
      t.textContent = '✓ ' + txt;
      setTimeout(function () { t.textContent = t.__sxLabel; t.__sxLabel = null; t.classList.remove('is-added'); }, 2800);
    }
    setTimeout(function () {
      if (btn) btn.classList.remove('is-added');
      if (qa.classList.contains('is-open')) closeAll(true);
    }, 750);
  }

  // One add at a time, page-wide: a second tap while an add is in flight is ignored (the double-add guard).
  var addBusy = false;
  function addToCart(variantId, trigger, mode, qa) {
    var c = cfg();
    if (addBusy) return;
    var v2 = !!(qa && trigger && trigger.hasAttribute('data-sx-qa-add'));
    // Keevado outside the US: no add before the 7 Nov drop (9am LA, snippets/kv-world), open everywhere after it (2026-11-05, Owen).
    if (qa && qa.querySelector('[data-sx-qa-kv]') && window.Shopify && Shopify.country && Shopify.country !== 'US' && Date.now() < 1794070800000) return;
    addBusy = true;
    // Market-aware cart base — Shopify prefixes routes.cart_url with the market
    // subfolder (e.g. '/en-us/cart' on the US storefront, '/cart' on AU root).
    // Hardcoding the bare '/cart…' endpoints silently switched the US cart to AUD
    // (the add returns 200 but checkout is in the wrong currency). Build every cart
    // call from routes.cart_url, exactly like the gh3 PDP buy box does.
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    if (trigger) { trigger.classList.add('is-loading'); trigger.setAttribute('aria-disabled', 'true'); trigger.classList.remove('sx-qa__btn--error'); }
    if (v2) setErr(qa, '');
    var ok = false;
    // PERF 2026-09-08: the member-code apply below is skipped when the code is already on the cart
    // (Shopify serialises cart mutations, so it was a ~1s no-op on every add after the first).
    var codeOnCart = false;
    // PERF 2026-10-13 (cart speed): the drawer opens at the tap (pending state) and the add goes through
    // window.cartCommit (cart-drawer.js): add.js, then the member code in an update.js that renders the
    // drawer + bubble from its own response (market root, wallets kept), instead of add -> update -> two
    // section GETs. No drawer methods on the page -> today's chain below, unchanged.
    // v2: the sheet stays up under the drawer (its button shows the added state) and only its touch guard is
    // dropped at the tap, so the drawer scrolls.
    var drawer = document.querySelector('cart-drawer');
    var fast = !!(window.cartCommit && drawer && typeof drawer.beginPending === 'function');
    if (fast) {
      if (v2) document.removeEventListener('touchmove', qaTouchGuard);
      else closeAll();
      drawer.beginPending({ trigger: trigger });
    }
    // "one" = this item only: no membership line and no member code (Keevado one-time, as on its PDP).
    var oneTime = mode === 'one';
    // PROTECTION A — read the cart first; only enrol membership if not present.
    (window.__gh3CartGet ? window.__gh3CartGet() : fetch(cartUrl + '.js', { headers: { 'Content-Type': 'application/json' } }).then(function (r) { return r.json(); }))
      .then(function (cart) {
        if (cart && cart.discount_codes && c.code) {
          for (var d = 0; d < cart.discount_codes.length; d++) {
            if (String(cart.discount_codes[d].code).toUpperCase() === String(c.code).toUpperCase()) { codeOnCart = true; break; }
          }
        }
        var hasMember = false;
        if (cart && cart.items) {
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id === c.variantId || cart.items[i].product_id === c.productId) { hasMember = true; break; }
          }
        }
        var items = [{ id: parseInt(variantId, 10), quantity: 1 }];
        // PROTECTION B — enrol membership for NON-members only.
        if (!oneTime && !c.isMember && !hasMember && c.variantId) {
          items.push({ id: c.variantId, quantity: 1, selling_plan: c.sellingPlan });
        }
        var code = (oneTime || codeOnCart || !c.code) ? null : c.code;
        if (fast) {
          return window.cartCommit({ items: items, code: code, source: 'sx-quick-add', variantId: variantId }).then(function () { ok = true; });
        }
        return fetch(cartUrl + '/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function (resp) {
            if (resp && resp.status) { var e = new Error(resp.description || 'add failed'); e.status = resp.status; e.description = resp.description; throw e; }
            ok = true;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sx-quick-add', cartData: resp });
            }
            // Apply the member discount code (skipped when already on the cart), then open drawer.
            if (!code) { return openDrawer(); }
            return fetch(cartUrl + '/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: code })
            }).catch(function () {}).then(openDrawer);
          });
      })
      .catch(function (err) {
        console.error('sx-quick-add:', err);
        if (fast) drawer.failPending();
        if (trigger) trigger.classList.add('sx-qa__btn--error');
        if (v2) {
          var p = panelOf(qa);
          setErr(qa, (err && err.status && err.description) || (p && p.getAttribute('data-t-err')) || '');
          if (fast && qa.classList.contains('is-open') && !window.matchMedia('(min-width:750px)').matches) {
            document.addEventListener('touchmove', qaTouchGuard, { passive: false });
          }
        }
      })
      .finally(function () {
        addBusy = false;
        if (trigger) { trigger.classList.remove('is-loading'); trigger.removeAttribute('aria-disabled'); }
        if (!v2) closeAll();
        else if (ok) showAdded(qa, trigger);
      });
  }

  // animate = slide / fade the open sheet away first (close button, scrim, Escape, after an add).
  var closeTimer = 0;
  function closeAll(animate) {
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var open = document.querySelectorAll('.sx-qa.is-open:not(.is-closing)');
    if (animate === true && open.length && !still) {
      open.forEach(function (qa) { qa.classList.add('is-closing'); });
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { closeAll(); }, 190);
      return;
    }
    clearTimeout(closeTimer);
    document.querySelectorAll('.sx-qa.is-open, .sx-qa.is-closing').forEach(function (qa) {
      qa.classList.remove('is-open');
      qa.classList.remove('is-closing');
      var t = qa.querySelector('[data-sx-qa-open]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('sx-qa-lock');
    document.removeEventListener('touchmove', qaTouchGuard);
    // Re-show the collection "back to top" button, hidden while a picker was open.
    document.body.classList.remove('sx-qa-open');
    // Restore any card transforms we neutralised on open.
    document.querySelectorAll('[data-sx-qa-tf]').forEach(function (el) {
      el.style.removeProperty('transform');
      el.style.removeProperty('transition');
      el.style.removeProperty('animation');
      el.removeAttribute('data-sx-qa-tf');
    });
    // Restore lifted .quick-add stacking + clear the desktop inline positioning.
    document.querySelectorAll('[data-sx-qa-z]').forEach(function (el) {
      el.style.removeProperty('z-index');
      el.removeAttribute('data-sx-qa-z');
    });
    document.querySelectorAll('[data-sx-qa-panel]').forEach(function (p) {
      p.style.removeProperty('left');
      p.style.removeProperty('top');
      p.style.removeProperty('--sx-qa-ax');
      p.removeAttribute('data-sx-qa-place');
    });
  }

  // Filter the size grid to the currently-selected colour AND extra option (e.g.
  // sleeve). A size chip shows only when it matches BOTH, so 3-option products
  // ([Color | Size | Sleeve], the 48-variant merged tee) list each size once
  // instead of twice — and the size you tap resolves to a single variant.
  function applyFilter(qa) {
    if (!qa) return;
    var col = qa.getAttribute('data-sel-color');
    var ext = qa.getAttribute('data-sel-extra');
    var multi = !!qa.querySelector('[data-sx-qa-colors]');
    var hasExtra = !!qa.querySelector('[data-sx-qa-extras]');
    qa.querySelectorAll('.sx-qa__size').forEach(function (s) {
      var off = false;
      if (multi && col != null && s.getAttribute('data-sx-qa-color') !== col) off = true;
      if (hasExtra && ext != null && s.getAttribute('data-sx-qa-extra') !== ext) off = true;
      s.classList.toggle('sx-qa__size--off', off);
    });
    settleSelection(qa);
  }

  // colour|extra -> image (data-sx-qa-imgmap, e.g. "NAVY|Long Sleeve"). 2026-09-18: the
  // preview only knew one image per colour, so Long Sleeve kept the short-sleeve shot.
  function imgMap(qa) {
    if (qa.__sxImgMap !== undefined) return qa.__sxImgMap;
    var m = null, p = qa.querySelector('[data-sx-qa-imgmap]');
    if (p) { try { m = JSON.parse(p.getAttribute('data-sx-qa-imgmap')); } catch (e) { m = null; } }
    qa.__sxImgMap = m;
    return m;
  }
  function selColor(qa) {
    var c = qa.getAttribute('data-sel-color');
    if (c != null) return c;
    var p = qa.querySelector('[data-sx-qa-lock]');
    return p ? p.getAttribute('data-sx-qa-lock') : null;
  }
  function tileFor(qa, color) {
    var f = null;
    qa.querySelectorAll('.sx-qa__tile').forEach(function (b) { if (b.getAttribute('data-sx-qa-color') === color) f = b; });
    return f;
  }
  // image for a colour in the current extra: the pair's own image, else the colour's
  function colorImg(qa, color, extra) {
    var m = imgMap(qa);
    if (m && color != null && extra != null && m[color + '|' + extra]) return m[color + '|' + extra];
    var t = color != null ? tileFor(qa, color) : null;
    return t ? t.getAttribute('data-sx-qa-img') : null;
  }
  // does this colour have an available size in this extra? (no extra toggle = any size)
  function hasStock(qa, color, extra) {
    var hasExtra = !!qa.querySelector('[data-sx-qa-extras]');
    return Array.prototype.some.call(qa.querySelectorAll('.sx-qa__size'), function (s) {
      if (s.hasAttribute('disabled')) return false;
      if (color != null && s.hasAttribute('data-sx-qa-color') && s.getAttribute('data-sx-qa-color') !== color) return false;
      if (hasExtra && extra != null && s.getAttribute('data-sx-qa-extra') !== extra) return false;
      return true;
    });
  }
  // Preview + colour tiles follow the selected colour AND extra. Only ever runs from an
  // open or opening picker, so a collection page still loads no preview until a tap.
  function paintImages(qa) {
    var col = selColor(qa), ext = qa.getAttribute('data-sel-extra');
    var hasExtra = !!qa.querySelector('[data-sx-qa-extras]');
    qa.querySelectorAll('.sx-qa__tile').forEach(function (b) {
      var c = b.getAttribute('data-sx-qa-color');
      if (hasExtra && ext != null) {
        var sw = b.querySelector('.sx-qa__tile-sw'), u = colorImg(qa, c, ext);
        if (sw && u) { var bg = 'url("' + u + '")'; if (sw.style.backgroundImage !== bg) sw.style.backgroundImage = bg; }
        b.classList.toggle('is-oos', !hasStock(qa, c, ext));
      }
    });
    var img = qa.querySelector('[data-sx-qa-preview]');
    if (!img) return;
    var url = colorImg(qa, col, ext) || img.getAttribute('data-sx-qa-fallback');
    if (url && img.getAttribute('src') !== url) img.setAttribute('src', url);
  }

  // Mark the chosen colour active everywhere (card swatch + panel tile), re-filter
  // the size chips, and swap the preview image to that colour (fallback if none).
  function selectColor(qa, color) {
    if (!qa || color == null) return;
    qa.querySelectorAll('.sx-qa__tile').forEach(function (b) {
      var on = b.getAttribute('data-sx-qa-color') === color;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    qa.querySelectorAll('.sx-qa__sw').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-sx-qa-color') === color);
    });
    qa.setAttribute('data-sel-color', color);
    var cn = qa.querySelector('[data-sx-qa-cname]');
    if (cn) cn.textContent = color;
    applyFilter(qa);
    paintImages(qa);
  }

  // Pick an extra-option value (e.g. sleeve), re-filter the size grid, swap the images.
  function selectExtra(qa, val) {
    if (!qa || val == null) return;
    qa.querySelectorAll('.sx-qa__seg-btn').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-sx-qa-extra') === val);
    });
    qa.setAttribute('data-sel-extra', val);
    applyFilter(qa);
    paintImages(qa);
  }

  // Default the extra toggle to the first value with an available size IN THE SELECTED
  // COLOUR (else in any colour, else the first value) so the grid opens on buyable sizes.
  function primeExtra(qa) {
    if (!qa || !qa.querySelector('[data-sx-qa-extras]')) return;
    if (qa.querySelector('.sx-qa__seg-btn.is-active')) return;
    var col = selColor(qa), forColor = null, forAny = null, firstAny = null;
    qa.querySelectorAll('.sx-qa__seg-btn').forEach(function (b) {
      var val = b.getAttribute('data-sx-qa-extra');
      if (firstAny === null) firstAny = val;
      if (forColor === null && col != null && hasStock(qa, col, val)) forColor = val;
      if (forAny === null && hasStock(qa, null, val)) forAny = val;
    });
    selectExtra(qa, forColor || forAny || firstAny);
  }

  // Default a freshly-opened multi-colour panel to the first colour with an
  // available size (else the first colour) so a size grid is always visible.
  function primeColors(qa) {
    if (!qa || !qa.querySelector('[data-sx-qa-colors]')) return;
    if (qa.querySelector('.sx-qa__tile.is-active')) return;
    // 2026-10-18: a colour-split card's sheet opens on the card's own colour (then the shopper can switch).
    var lockP = qa.querySelector('[data-sx-qa-lock]');
    var lockC = lockP && lockP.getAttribute('data-sx-qa-lock');
    if (lockC != null && tileFor(qa, lockC)) { selectColor(qa, lockC); return; }
    var firstAvail = null, firstAny = null;
    qa.querySelectorAll('.sx-qa__tile').forEach(function (cb) {
      var col = cb.getAttribute('data-sx-qa-color');
      if (firstAny === null) firstAny = col;
      if (firstAvail === null) {
        var ok = Array.prototype.some.call(qa.querySelectorAll('.sx-qa__size'), function (s) {
          return s.getAttribute('data-sx-qa-color') === col && !s.hasAttribute('disabled');
        });
        if (ok) firstAvail = col;
      }
    });
    selectColor(qa, firstAvail || firstAny);
  }

  // Neutralise the owning card's transform (Dawn perspective) and lift its
  // stacking context. A transformed ancestor makes the fixed panel anchor to the
  // CARD (mis-placed) and z-traps it under the sticky filter bar; killing the
  // transform + raising the card's .quick-add z-index lets the panel anchor to
  // the VIEWPORT and paint on top. Inline !important beats all CSS; the live
  // transition is killed FIRST or it keeps re-applying the transform. All
  // restored in closeAll. Applied on BOTH mobile (bottom-sheet) and desktop
  // (popover) since both are position:fixed.
  function neutralizeAnchors(qa) {
    ['.card-wrapper', '.card'].forEach(function (sel) {
      var el = qa.closest(sel);
      if (el) {
        el.style.setProperty('transition', 'none', 'important');
        el.style.setProperty('animation', 'none', 'important');
        el.style.setProperty('transform', 'none', 'important');
        el.setAttribute('data-sx-qa-tf', '');
      }
    });
    var q = qa.closest('.quick-add');
    if (q) { q.style.setProperty('z-index', '200', 'important'); q.setAttribute('data-sx-qa-z', ''); }
  }

  // Desktop: place the fixed panel above the trigger (or below if there's no room)
  // and clamp it inside the viewport, so it never overhangs the card onto the next
  // product nor runs off the right edge. JS writes inline left/top in viewport
  // coords (valid because the card transform was neutralised above).
  function positionDesktop(qa) {
    var panel = qa.querySelector('[data-sx-qa-panel]');
    var trig = qa.querySelector('[data-sx-qa-open]') || qa.querySelector('.sx-qa__btn') || qa;
    if (!panel) return;
    panel.style.left = '-9999px'; panel.style.top = '0px';   // measure off-screen
    var pw = panel.offsetWidth, ph = panel.offsetHeight;
    var r = trig.getBoundingClientRect();
    var vw = document.documentElement.clientWidth, vh = window.innerHeight, m = 12, gap = 8;
    var left = r.left + r.width / 2 - pw / 2;                 // centre on the trigger
    if (left + pw > vw - m) left = vw - m - pw;
    if (left < m) left = m;
    var place = 'above', top;
    if (r.top - gap - ph >= m) { top = r.top - gap - ph; place = 'above'; }
    else if (r.bottom + gap + ph <= vh - m) { top = r.bottom + gap; place = 'below'; }
    else { top = Math.max(m, vh - m - ph); place = 'above'; }
    panel.style.left = Math.round(left) + 'px';
    panel.style.top = Math.round(top) + 'px';
    panel.style.setProperty('--sx-qa-ax', Math.max(14, Math.min(pw - 14, r.left + r.width / 2 - left)) + 'px');
    panel.setAttribute('data-sx-qa-place', place);
  }

  // The panel is `display:none` until it opens, so a lazy <img> inside it never
  // intersects the viewport and Chrome never loads it — every preview was blank.
  // The markup therefore ships NO src; point the image at the active colour (or the
  // product photo) the moment we need it. 2026-08-28.
  function primePreview(qa) {
    var img = qa && qa.querySelector('[data-sx-qa-preview]');
    if (!img || img.getAttribute('src')) return;
    var active = qa.querySelector('.sx-qa__tile.is-active');
    var url = (active && active.getAttribute('data-sx-qa-img')) || img.getAttribute('data-sx-qa-fallback');
    if (url) img.setAttribute('src', url);
  }

  // iOS ignores overflow:hidden on the root, so a swipe over the scrim still panned
  // the collection behind the open sheet. Same guard the cart drawer uses: swallow any
  // touchmove that isn't inside a panel scroller that actually overflows.
  function qaTouchGuard(e) {
    var panel = document.querySelector('.sx-qa.is-open .sx-qa__panel');
    var node = e.target;
    while (node && node !== document.documentElement) {
      if (node.nodeType === 1 && panel && panel.contains(node)) {
        var st = getComputedStyle(node);
        if (/(auto|scroll)/.test(st.overflowY) && node.scrollHeight > node.clientHeight + 1) return;
      }
      node = node.parentElement;
    }
    e.preventDefault();
  }

  function openPanel(qa, preColor) {
    closeAll();
    sortSizes(qa);
    qa.classList.add('is-open');
    // Hide the floating "back to top" button so it can't overlap the picker
    // (it sits at z-index:999, above the panel) — restored in closeAll.
    document.body.classList.add('sx-qa-open');
    var ob = qa.querySelector('[data-sx-qa-open]');
    if (ob) ob.setAttribute('aria-expanded', 'true');
    // colour first (a tapped card swatch wins), then the sleeve that colour actually has
    if (preColor != null) selectColor(qa, preColor);
    else primeColors(qa);
    primeExtra(qa);
    applyFilter(qa);
    primePreview(qa);
    neutralizeAnchors(qa);
    if (window.matchMedia('(min-width:750px)').matches) {
      positionDesktop(qa);
    } else {
      document.body.classList.add('sx-qa-lock');
      document.addEventListener('touchmove', qaTouchGuard, { passive: false });
    }
  }

  document.addEventListener('click', function (e) {
    // "+N" chip — open the picker so all colours are visible.
    var moreBtn = e.target.closest('[data-sx-qa-more]');
    if (moreBtn) {
      e.preventDefault();
      var mqa = moreBtn.closest('.sx-qa');
      if (mqa) openPanel(mqa, null);
      return;
    }
    // Card colour swatch — open the picker focused on that colour.
    var swatch = e.target.closest('.sx-qa__sw');
    if (swatch) {
      e.preventDefault();
      var sqa = swatch.closest('.sx-qa');
      if (sqa) openPanel(sqa, swatch.getAttribute('data-sx-qa-color'));
      return;
    }
    // Add-to-cart trigger — toggle the picker.
    var openBtn = e.target.closest('[data-sx-qa-open]');
    if (openBtn) {
      e.preventDefault();
      var qa = openBtn.closest('.sx-qa');
      if (!qa) return;
      if (qa.classList.contains('is-open')) closeAll(true);
      else openPanel(qa, null);
      return;
    }
    // Colour tile inside the panel — filter sizes + swap preview (don't add).
    var tileBtn = e.target.closest('.sx-qa__tile');
    if (tileBtn) {
      e.preventDefault();
      selectColor(tileBtn.closest('.sx-qa'), tileBtn.getAttribute('data-sx-qa-color'));
      return;
    }
    // Extra-option toggle (e.g. sleeve) — re-filter sizes (don't add).
    var segBtn = e.target.closest('.sx-qa__seg-btn');
    if (segBtn) {
      e.preventDefault();
      selectExtra(segBtn.closest('.sx-qa'), segBtn.getAttribute('data-sx-qa-extra'));
      return;
    }
    // v2 footer button — add the picked size (no size yet: point at the size grid).
    var addBtn = e.target.closest('[data-sx-qa-add]');
    if (addBtn) {
      e.preventDefault();
      var aqa = addBtn.closest('.sx-qa');
      if (!aqa || addBtn.getAttribute('aria-disabled') === 'true' || addBtn.classList.contains('is-added')) return;
      var picked = selectedSize(aqa);
      if (!picked) { nudge(aqa); return; }
      addToCart(picked.getAttribute('data-sx-qa-variant'), addBtn, addBtn.getAttribute('data-sx-qa-add'), aqa);
      return;
    }
    // Size chip: v2 selects it; a legacy panel (or the card's direct one-size add) adds straight away.
    var sizeBtn = e.target.closest('[data-sx-qa-variant]');
    if (sizeBtn) {
      e.preventDefault();
      if (sizeBtn.hasAttribute('disabled') || sizeBtn.getAttribute('aria-disabled') === 'true') return;
      var zqa = sizeBtn.closest('.sx-qa');
      if (sizeBtn.classList.contains('sx-qa__size') && isV2(zqa)) { selectSize(zqa, sizeBtn); return; }
      addToCart(sizeBtn.getAttribute('data-sx-qa-variant'), sizeBtn);
      return;
    }
    // outside click / explicit close
    if (e.target.closest('[data-sx-qa-close]') || !e.target.closest('.sx-qa__panel')) {
      closeAll(e.target.closest('cart-drawer') ? false : true);
    }
  });

  // Warm the preview on hover so a desktop popover never opens to an empty frame.
  document.addEventListener('pointerover', function (e) {
    if (!e.target.closest) return;
    var qa = e.target.closest('.sx-qa');
    if (qa) primePreview(qa);
  }, { passive: true });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') closeAll(true);
  });

  // Keep the desktop popover glued to its trigger while the page scrolls/resizes;
  // close it if the trigger scrolls out of view. (Mobile is a pinned bottom-sheet
  // and needs none of this.) rAF-throttled so scroll stays smooth.
  var repoPending = false;
  function repositionOpen() {
    var qa = document.querySelector('.sx-qa.is-open');
    if (!qa || !window.matchMedia('(min-width:750px)').matches) return;
    var trig = qa.querySelector('[data-sx-qa-open]') || qa.querySelector('.sx-qa__btn');
    if (trig) {
      var r = trig.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) { closeAll(); return; }
    }
    positionDesktop(qa);
  }
  window.addEventListener('scroll', function () {
    if (repoPending) return;
    repoPending = true;
    requestAnimationFrame(function () { repoPending = false; repositionOpen(); });
  }, { passive: true });
  window.addEventListener('resize', repositionOpen);
})();
